import type { admin as en } from "../en/admin";
import type { Messages } from "../types";

export const admin: Messages<typeof en> = {
  title: "إدارة المنصة",
  badge: "للفريق فقط",
  subtitle: "إدارة ملفك: المستخدمون والنماذج والتدفقات ومفاتيح الإيقاف. كل تغيير يُسجَّل في سجل التدقيق.",
  nav: {
    label: "أقسام الإدارة",
    overview: "نظرة عامة",
    users: "المستخدمون",
    orgs: "المؤسسات",
    ai: "نماذج الذكاء الاصطناعي",
    flows: "التدفقات والطوابير",
    audit: "سجل التدقيق",
    settings: "الإعدادات ومفاتيح الإيقاف",
    plans: "الخطط والحدود",
  },
  status: {
    ok: "كل الأنظمة تعمل",
    degraded: "أداء منخفض",
    down: "انقطاع",
    checking: "جارٍ الفحص…",
    live: "مباشر",
    paused: "متوقف مؤقتًا",
    switchesOn_zero: "مفاتيح إيقاف مفعّلة: {{count}}",
    switchesOn_one: "مفاتيح إيقاف مفعّلة: {{count}}",
    switchesOn_two: "مفاتيح إيقاف مفعّلة: {{count}}",
    switchesOn_few: "مفاتيح إيقاف مفعّلة: {{count}}",
    switchesOn_many: "مفاتيح إيقاف مفعّلة: {{count}}",
    switchesOn_other: "مفاتيح إيقاف مفعّلة: {{count}}",
  },
  common: {
    copy: "نسخ {{what}}",
    copied: "تم النسخ",
    workflowId: "معرّف سير العمل",
    requestId: "معرّف الطلب",
    openTemporal: "فتح في Temporal",
    openLangfuse: "فتح التتبّع في Langfuse",
    none: "—",
    reason: "السبب",
    reasonPlaceholder: "لماذا تقوم بهذا الإجراء؟ (يُحفظ في سجل التدقيق)",
    reasonRequired: "اكتب 3 أحرف على الأقل.",
    review: "مراجعة",
    payload: "البيانات المستلمة",
    before: "قبل",
    after: "بعد",
    loadMore: "تحميل المزيد",
    all: "الكل",
    from: "من",
    to: "إلى",
    filters: "عوامل التصفية",
    clearFilters: "مسح عوامل التصفية",
    noResults: "لا شيء يطابق عوامل التصفية هذه.",
    changedBy: "غيّره {{who}} · {{when}}",
    neverChanged: "لم يتغيّر قط",
    degraded: "غير متاح جزئيًا",
    auditNote: "يُسجَّل هذا الإجراء في سجل التدقيق باسمك.",
    typeToConfirm: "اكتب {{word}} للتأكيد",
    you: "أنت",
  },
  overview: {
    title: "نظرة عامة",
    description: "صحة كل خدمة، والطوابير المباشرة، ونشاط اليوم.",
    health: "الخدمات المعتمَد عليها",
    healthHint: "يفحصها الخادم كل بضع ثوانٍ. تعطّل خدمة إلزامية يجعل المنصة غير جاهزة.",
    required: "إلزامية",
    optional: "اختيارية",
    latency: "{{ms}} مللي ثانية",
    queues: "الطوابير المباشرة",
    queuesHint: "المتراكم في كل طابور Temporal وصندوق الويب هوك والعمليات.",
    backlog: "متراكم",
    pollers: "عمّال",
    running: "قيد التشغيل",
    unknown: "غير متاح",
    webhookInbox: "صندوق الويب هوك",
    unprocessed: "غير معالَجة",
    failed24h: "فشلت (24 س)",
    oldest: "الأقدم انتظارًا",
    operations: "العمليات",
    queued: "عمليات جارية",
    approvalsPending: "موافقات بالانتظار",
    activity: "آخر 24 ساعة",
    activityHint: "الويب هوك المستلمة وتشغيلات الأتمتة والإخفاقات في كل ساعة.",
    series: { webhooks: "ويب هوك", runs: "تشغيلات", failures: "إخفاقات" },
    versions: "الإصدارات",
    versionApi: "الواجهة البرمجية",
    versionNode: "Node",
    versionEnv: "البيئة",
    versionSchema: "المخطط",
    tools: "الأدوات",
    toolHint: { temporalUi: "سير العمل والسجل والطوابير", langfuse: "تتبّع نماذج اللغة والقوالب", litellm: "إدارة بوابة النماذج" },
    toolName: { temporalUi: "Temporal", langfuse: "Langfuse", litellm: "LiteLLM" },
    notConfigured: "غير مُعدّ",
    switches: "مفاتيح الإيقاف",
    allSwitchesOff: "كل شيء يعمل بشكل طبيعي.",
    manageSwitches: "إدارة المفاتيح",
    semantic: {
      title: "فهرس البحث الدلالي",
      hint: "Dify Knowledge: ما يستطيع البحث الذكي والمساعد العثور عليه. الأرقام من آخر فحص لكل مؤسسة.",
      dep: "الفهرس الدلالي",
      state: { ok: "الفهرس الدلالي: سليم", degraded: "الفهرس الدلالي: متأثر جزئيًا", unavailable: "الفهرس الدلالي: غير متاح" },
      notConfigured: "غير مُعدّ: البحث الذكي يستخدم البحث بالكلمات.",
      unreachable: "لا يمكن الوصول إلى Dify Knowledge.",
      indexed: "مفهرس",
      indexing: "قيد الفهرسة",
      failed: "فشل",
      lastError: "آخر خطأ",
      orgsWithErrors: "مؤسسات فيها مستندات فاشلة",
      reindexAll: "إعادة فهرسة الكل",
      reindex: "إعادة الفهرسة",
      started: "بدأت إعادة الفهرسة: تُرسل المستندات الفاشلة من جديد.",
      checkedAt: "فُحص {{when}}",
    },
    kpi: {
      users: "المستخدمون",
      orgs: "المؤسسات",
      accounts: "حسابات القنوات",
      webhooks24h: "ويب هوك (24 س)",
      runs24h: "تشغيلات الأتمتة (24 س)",
      failedRuns24h: "تشغيلات فاشلة (24 س)",
      externalActions24h: "إجراءات القنوات (24 س)",
      disabled: "{{n}} معطّل",
      reauth: "{{n}} يحتاج إعادة ربط",
      failureRate: "نسبة الفشل {{pct}}",
    },
    checkedAt: "فُحص {{when}}",
    livePaused: "يتوقف التحديث المباشر عندما تكون هذه الصفحة مخفية.",
  },
  users: {
    title: "المستخدمون",
    description: "كل من لديه حساب دخول إلى ملفك، في كل المؤسسات.",
    search: "ابحث بالاسم أو البريد",
    create: "إنشاء مستخدم",
    filterStatus: "الحالة",
    filterRole: "دور المنصة",
    statusAll: "أي حالة",
    roleAll: "الجميع",
    roleAdmins: "مدراء المنصة",
    active: "نشط",
    disabled: "معطّل",
    platformAdmin: "مدير المنصة",
    envAdmin: "محدَّد عبر PLATFORM_ADMIN_EMAILS: لا يمكن تغييره من هنا",
    col: { user: "المستخدم", orgs: "المؤسسات", status: "الحالة", lastLogin: "آخر دخول", sessions: "الجلسات", created: "تاريخ الإنشاء", actions: "الإجراءات" },
    neverSignedIn: "لم يدخل قط",
    noOrgs: "بلا مؤسسة",
    actions: {
      menu: "إجراءات {{name}}",
      disable: "تعطيل",
      enable: "تفعيل",
      resetPassword: "إعادة تعيين كلمة المرور",
      grantAdmin: "تعيين مديرًا للمنصة",
      revokeAdmin: "إزالة صلاحية مدير المنصة",
    },
    confirm: {
      disableTitle: "تعطيل {{name}}؟",
      disableBody: "سيُسجَّل خروجه من كل الأجهزة ولن يستطيع الدخول حتى يُفعَّل مجددًا. تبقى بياناته محفوظة.",
      enableTitle: "تفعيل {{name}}؟",
      enableBody: "يستطيع الدخول مجددًا بكلمة مروره.",
      resetTitle: "إعادة تعيين كلمة مرور {{name}}؟",
      resetBody: "تنتهي جلساته وتُنشأ كلمة مرور مؤقتة تُعرض لمرة واحدة فقط.",
      grantTitle: "تعيين {{name}} مديرًا للمنصة؟",
      grantBody: "سيرى هذه اللوحة ويستطيع تغيير أي شيء في المنصة.",
      revokeTitle: "إزالة صلاحية مدير المنصة من {{name}}؟",
      revokeBody: "يفقد الوصول إلى هذه اللوحة فورًا.",
    },
    created: "تم إنشاء المستخدم",
    updated: "تم تحديث المستخدم",
    tempPassword: {
      title: "كلمة مرور مؤقتة",
      body: "أرسلها إلى {{email}} عبر قناة آمنة. تُعرض هذه المرة فقط، ويجب تغييرها بعد الدخول.",
      once: "تُعرض مرة واحدة ولن تظهر مجددًا.",
      copy: "نسخ كلمة المرور",
      done: "حفظتها",
      reveal: "إظهار",
      hide: "إخفاء",
    },
    form: {
      title: "إنشاء مستخدم",
      description: "سيحصل على كلمة مرور مؤقتة لمرة واحدة.",
      email: "البريد الإلكتروني",
      name: "الاسم الكامل",
      locale: "اللغة",
      org: "المؤسسة",
      orgMode: "المؤسسة",
      orgExisting: "مؤسسة موجودة",
      orgNew: "مؤسسة جديدة",
      orgSearch: "ابحث في المؤسسات",
      orgPick: "اختر مؤسسة",
      orgNoMatch: "لا توجد مؤسسة مطابقة",
      orgRequired: "اختر مؤسسة: كل مستخدم ينتمي إلى مؤسسة.",
      newOrgName: "اسم المؤسسة",
      newOrgNameRequired: "أدخل اسم المؤسسة.",
      newOrgOwner: "سيكون المستخدم الجديد مالكها.",
      plan: "الخطة",
      role: "الدور في تلك المؤسسة",
      platformAdmin: "مدير المنصة",
      platformAdminHint: "وصول كامل إلى هذه اللوحة. استخدمه بحذر.",
      emailInvalid: "أدخل بريدًا إلكترونيًا صحيحًا.",
      nameRequired: "أدخل الاسم.",
    },
    empty: "لا يوجد مستخدمون مطابقون.",
  },
  orgs: {
    title: "المؤسسات",
    description: "مساحات العمل والخطط والأعضاء ومفاتيح كل مؤسسة.",
    search: "ابحث بالاسم أو المعرّف",
    planAll: "أي خطة",
    col: { org: "المؤسسة", plan: "الخطة", members: "الأعضاء", accounts: "الحسابات", owner: "المالك", created: "الإنشاء", overrides: "استثناءات" },
    plans: { trial: "تجريبية", free: "مجانية", pro: "احترافية", business: "أعمال" },
    create: {
      button: "إنشاء مؤسسة",
      title: "إنشاء مؤسسة",
      description: "الاسم والخطة والمالك. يُسجَّل في سجل التدقيق.",
      name: "الاسم",
      nameRequired: "أدخل الاسم.",
      plan: "الخطة",
      owner: "المالك",
      ownerExisting: "مستخدم موجود",
      ownerNew: "مستخدم جديد",
      ownerSearch: "ابحث في المستخدمين",
      ownerPick: "اختر المالك",
      ownerNoMatch: "لا يوجد مستخدم مطابق",
      ownerRequired: "اختر المالك.",
      ownerEmail: "بريد المالك",
      ownerName: "اسم المالك",
      ownerLocale: "لغة المالك",
      reviewTitle: "إنشاء {{name}}؟",
      reviewBody: "الخطة: {{plan}} · المالك: {{owner}}",
      confirm: "إنشاء",
      created: "تم إنشاء المؤسسة",
    },
    roles: { owner: "مالك", admin: "مدير", editor: "محرر", viewer: "مشاهد" },
    select: "تحديد {{name}}",
    selected_zero: "المؤسسات المحددة: {{count}}",
    selected_one: "المؤسسات المحددة: {{count}}",
    selected_two: "المؤسسات المحددة: {{count}}",
    selected_few: "المؤسسات المحددة: {{count}}",
    selected_many: "المؤسسات المحددة: {{count}}",
    selected_other: "المؤسسات المحددة: {{count}}",
    clear: "إلغاء التحديد",
    applyPlan: "تطبيق خطة",
    applyTitle: "تطبيق خطة على المؤسسات المحددة",
    applyBody: "تتغير ميزاتها وحدودها فوراً (تبقى التخصيصات).",
    applyDone: "طُبقت الخطة على {{updated}} مؤسسات",
    planLink: "الخطة والصلاحيات",
    usage: "الاستخدام",
    noOwner: "بلا مالك",
    open: "فتح {{name}}",
    detail: {
      members: "الأعضاء",
      accounts: "حسابات القنوات",
      usage: "الاستخدام (30 يومًا)",
      comments30d: "التعليقات",
      runs30d: "تشغيلات الأتمتة",
      failedRuns30d: "تشغيلات فاشلة",
      plan: "الخطة",
      name: "الاسم",
      saveDetails: "حفظ التفاصيل",
      addMember: "إضافة عضو",
      addMemberHint: "اختر مستخدمًا ودورًا. تبقى لكل مؤسسة مالك واحد على الأقل.",
      pickUser: "اختر مستخدمًا",
      remove: "إزالة {{name}}",
      removeTitle: "إزالة {{name}} من {{org}}؟",
      removeBody: "يفقد الوصول إلى هذه المؤسسة فورًا.",
      roleFor: "دور {{name}}",
      switches: "مفاتيح هذه المؤسسة",
      switchesHint: "الاستثناءات تُضاف إلى المفاتيح العامة: إن كان أحدهما مفعّلًا فهو مفعّل.",
      inherit: "حسب العام",
      on: "مفعّل",
      off: "متوقف",
      noAccounts: "لا حسابات مرتبطة.",
      aiOn: "الذكاء مفعّل",
      aiOff: "الذكاء متوقف",
      saved: "تم تحديث المؤسسة",
    },
    empty: "لا توجد مؤسسات مطابقة.",
  },
  ai: {
    title: "نماذج الذكاء الاصطناعي",
    description: "التطبيق يستدعي الأسماء المستعارة فقط (malfak-*). وجّه أي اسم إلى نموذج آخر أثناء التشغيل دون إعادة نشر.",
    gateway: "بوابة LiteLLM",
    reachable: "متصلة",
    unreachable: "غير متصلة",
    manageable: "التعديل أثناء التشغيل مفعّل",
    readOnly: "للقراءة فقط",
    readOnlyHint: "تغيير النماذج يتطلب تشغيل LiteLLM مع STORE_MODEL_IN_DB=True وقاعدة بيانات. الأسماء المستعارة ما زالت ظاهرة.",
    masterKey: "المفتاح الرئيسي",
    keyMissing: "غير مُعدّ",
    keyNote: "يبقى المفتاح على الخادم؛ يظهر جزء منه فقط.",
    aliases: "الأسماء المستعارة",
    aliasesHint: "ما يستدعيه كل اسم الآن.",
    deployments_zero: "عمليات النشر: {{count}}",
    deployments_one: "عمليات النشر: {{count}}",
    deployments_two: "عمليات النشر: {{count}}",
    deployments_few: "عمليات النشر: {{count}}",
    deployments_many: "عمليات النشر: {{count}}",
    deployments_other: "عمليات النشر: {{count}}",
    fallbacks: "البدائل",
    noFallbacks: "بلا بدائل",
    switch: "تبديل النموذج",
    switchTitle: "تبديل {{alias}}",
    switchDescription: "كل ميزة تستخدم {{alias}} تبدأ باستخدام النموذج الجديد خلال ثوانٍ.",
    switchTo: "نموذج المزوّد الجديد",
    switchToHint: "معرّف النموذج في LiteLLM، مثل openai/gpt-5-mini",
    pickKnown: "أو اختر نموذجًا مستخدمًا",
    beforeAfter: "التغيير",
    switched: "{{alias}} يستخدم الآن {{model}}",
    warnings: "تنبيه",
    models: "النشرات",
    modelsHint: "كل نشرة خلف كل اسم. نشرات ملف الإعداد للقراءة فقط.",
    col: { alias: "الاسم", model: "نموذج المزوّد", provider: "المزوّد", mode: "النوع", tags: "الوسوم", source: "المصدر", cost: "التكلفة / مليون رمز", actions: "الإجراءات" },
    source: { config: "config.yaml", db: "أثناء التشغيل" },
    costIo: "إدخال {{in}} · إخراج {{out}}",
    add: "إضافة نشرة",
    addTitle: "إضافة نشرة",
    addDescription: "يضيف نموذجًا خلف اسم مستعار. تُشار بيانات الاعتماد باسم متغير البيئة: لا تُقبل المفاتيح الخام أبدًا.",
    fields: {
      alias: "الاسم المستعار",
      model: "نموذج المزوّد",
      credentialRef: "مرجع بيانات الاعتماد",
      credentialRefHint: "مثل os.environ/LLM_API_KEY (مُعدّ مسبقًا في البوابة)",
      apiBaseRef: "مرجع عنوان الواجهة",
      apiBaseRefHint: "مثل os.environ/LLM_API_BASE",
      mode: "النوع",
      tags: "الوسوم",
      tagsHint: "مفصولة بفواصل، مثل tier:cheap, lang:ar",
    },
    invalidRef: "استخدم الصيغة os.environ/NAME",
    invalidAlias: "أحرف إنجليزية صغيرة وأرقام و . _ - (2–63 حرفًا)",
    invalidTag: "الوسوم أحرف إنجليزية صغيرة وأرقام و : _ . -",
    editTags: "تعديل وسوم {{model}}",
    tagsSaved: "تم حفظ الوسوم",
    delete: "إزالة النشرة",
    deleteTitle: "إزالة {{model}} من {{alias}}؟",
    deleteBody: "تتوقف الطلبات إلى {{alias}} عن الذهاب لهذه النشرة. إن كانت الأخيرة فسيتوقف الاسم عن العمل.",
    deleted: "تمت إزالة النشرة",
    added: "تمت إضافة النشرة",
    readOnlyRow: "معرّفة في config.yaml",
    spend: "الإنفاق",
    spendHint: "من سجلات الإنفاق في البوابة.",
    spendDays: "آخر {{n}} يومًا",
    spendTotal: "الإجمالي",
    spendByModel: "حسب النموذج",
    spendUnavailable: "الإنفاق غير متاح",
    requests: "{{n}} طلب",
    modes: { chat: "محادثة", embedding: "تضمين", audio_transcription: "تحويل الكلام إلى نص", audio_speech: "تحويل النص إلى كلام", image_generation: "صور" },
  },
  flows: {
    title: "التدفقات والطوابير",
    description: "تتبّع أي حدث من البداية للنهاية: ويب هوك ← سير عمل ← قاعدة ← إجراء، مع روابط للتتبّع.",
    tabs: { webhooks: "صندوق الويب هوك", workflows: "سير العمل", runs: "تشغيلات الأتمتة", errors: "الأخطاء" },
    webhooks: {
      status: { received: "مستلم", processed: "معالَج", failed: "فشل" },
      col: { event: "الحدث", status: "الحالة", source: "المصدر", received: "الاستلام", workflow: "سير العمل" },
      filterStatus: "الحالة",
      filterObject: "الكائن",
      filterSource: "المصدر",
      search: "ابحث بمفتاح التكرار أو الخطأ",
      replays_zero: "إعادات: {{count}}",
      replays_one: "إعادات: {{count}}",
      replays_two: "إعادات: {{count}}",
      replays_few: "إعادات: {{count}}",
      replays_many: "إعادات: {{count}}",
      replays_other: "إعادات: {{count}}",
      signedBy: "موقّع من",
      processedAt: "عولج",
      error: "الخطأ",
      runs: "تشغيلات الأتمتة لهذا الحدث",
      noRuns: "لم تُشغَّل أي أتمتة لهذا الحدث.",
      history: "سجل الإعادة",
      noHistory: "لم يُعَد قط.",
      replay: "إعادة التشغيل",
      replayTitle: "إعادة تشغيل هذا الحدث؟",
      replayBody: "يُعاد تشغيل HandleInboundEvent للبيانات المحفوظة. إجراءات القنوات متكررة الأمان، فلن يُرسل شيء مرتين.",
      replayed: "بدأت الإعادة: {{id}}",
      replayNotStarted: "حُفظ لكن Temporal لم يبدأه: {{error}}",
      empty: "لا أحداث ويب هوك مطابقة.",
    },
    workflows: {
      unavailable: "Temporal غير متاح",
      unavailableHint: "ستعود القائمة عندما يتصل الخادم بـ Temporal. تبقى العمليات في الانتظار حتى ذلك الحين.",
      status: {
        RUNNING: "قيد التشغيل",
        COMPLETED: "مكتمل",
        FAILED: "فشل",
        CANCELLED: "أُلغي",
        TERMINATED: "أُنهي",
        CONTINUED_AS_NEW: "استمر كجديد",
        TIMED_OUT: "انتهت المهلة",
        UNKNOWN: "غير معروف",
      },
      col: { workflow: "سير العمل", type: "النوع", queue: "الطابور", status: "الحالة", started: "البدء", duration: "المدة" },
      filterQueue: "الطابور",
      filterStatus: "الحالة",
      filterType: "النوع",
      search: "معرّف سير العمل يبدأ بـ…",
      history: "السجل",
      historyTruncated: "تُعرض الأحداث الأولى فقط.",
      pending: "أنشطة معلّقة",
      attempt: "المحاولة {{n}}",
      input: "المدخلات",
      result: "النتيجة",
      failure: "الفشل",
      memo: "ملاحظات",
      events_zero: "الأحداث: {{count}}",
      events_one: "الأحداث: {{count}}",
      events_two: "الأحداث: {{count}}",
      events_few: "الأحداث: {{count}}",
      events_many: "الأحداث: {{count}}",
      events_other: "الأحداث: {{count}}",
      cancel: "إلغاء",
      terminate: "إنهاء",
      retry: "إعادة المحاولة",
      cancelTitle: "إلغاء {{id}}؟",
      cancelBody: "يطلب من سير العمل التوقف؛ يمكنه التنظيف أولًا.",
      terminateTitle: "إنهاء {{id}}؟",
      terminateBody: "يوقفه فورًا دون تنظيف. استخدمه فقط عندما لا ينجح الإلغاء.",
      retryTitle: "إعادة محاولة {{id}}؟",
      retryBody: "يعيده إلى أول مهمة: يُشغَّل مجددًا بنفس المدخلات والمعرّف.",
      done: { cancel: "طُلب الإلغاء", terminate: "تم إنهاء سير العمل", retry: "أُعيد تعيين سير العمل: التشغيل {{runId}}" },
      failed: "رفض Temporal: {{error}}",
      empty: "لا يوجد سير عمل مطابق.",
      next: "الصفحة التالية",
      first: "الصفحة الأولى",
    },
    runs: {
      col: { when: "الوقت", org: "المؤسسة", rule: "القاعدة", event: "الحدث", result: "النتيجة", reason: "السبب" },
      filterOrg: "المؤسسة",
      filterResult: "النتيجة",
      result: { executed: "نُفّذ", pending_approval: "بانتظار الموافقة", skipped: "تُخطّي", failed: "فشل", suggested: "مقترح" },
      noRule: "لم تطابق أي قاعدة",
      empty: "لا تشغيلات مطابقة.",
    },
    errors: {
      kind: { webhook: "ويب هوك", operation: "عملية", external_action: "إجراء قناة", automation_run: "تشغيل أتمتة" },
      empty: "لا إخفاقات حديثة. 🎉",
      hint: "أحدث الإخفاقات المحفوظة في المنصة.",
    },
  },
  audit: {
    title: "سجل التدقيق",
    description: "من فعل ماذا ومتى، مع القيم قبل وبعد. لا يمكن تعديل السجلات.",
    search: "ابحث بالهدف أو الإجراء",
    actor: "معرّف المنفّذ أو بريده",
    org: "المؤسسة",
    action: "الإجراء يبدأ بـ",
    col: { when: "الوقت", actor: "المنفّذ", action: "الإجراء", target: "الهدف", org: "المؤسسة" },
    system: "النظام",
    details: "عرض التفاصيل",
    hideDetails: "إخفاء التفاصيل",
    changes: "التغييرات",
    noChanges: "لم تُسجَّل قيم قبل/بعد.",
    empty: "لا سجلات مطابقة.",
    platform: "المنصة",
  },
  settings: {
    title: "الإعدادات ومفاتيح الإيقاف",
    description: "مفاتيح على مستوى المنصة. تسري خلال ثوانٍ في الخادم والعامل.",
    danger: "منطقة الخطر",
    dangerHint: "توقف هذه المفاتيح عملًا حقيقيًا لكل العملاء. كل تغيير يُسجَّل.",
    on: "مفعّل",
    off: "متوقف",
    turnOn: "تفعيل",
    turnOff: "إيقاف",
    switches: {
      automationPaused: {
        title: "إيقاف كل الأتمتة",
        body: "تتوقف القواعد عن العمل. تبقى التعليقات والرسائل الواردة محفوظة، ويُسجَّل كل تشغيل كمُتخطّى مع السبب.",
        onLabel: "الأتمتة متوقفة",
      },
      aiDisabled: {
        title: "إيقاف الذكاء الاصطناعي",
        body: "لا استدعاءات لنماذج اللغة. ميزات الذكاء تجيب بأنها غير متاحة، والأتمتة تعود لقواعد الكلمات وترسل الإجراءات المعتمدة على الذكاء لموافقة بشرية.",
        onLabel: "الذكاء الاصطناعي متوقف",
      },
      outboundDryRun: {
        title: "تشغيل تجريبي للإرسال",
        body: "لا يُرسل شيء إلى القنوات: الردود والرسائل والإخفاء والنشر تُسجَّل ولا تُنفَّذ.",
        onLabel: "تجريبي: لا يُرسل شيء",
      },
    },
    confirmOnTitle: "تفعيل «\u2068{{name}}\u2069» للجميع؟",
    confirmOffTitle: "إيقاف «\u2068{{name}}\u2069»؟",
    confirmOffBody: "يعود العمل الطبيعي لكل مؤسسة ليس لديها استثناء.",
    changed: "تم تحديث المفتاح",
    banner: "شريط الصيانة",
    bannerHint: "يظهر أعلى التطبيق لكل مستخدم مسجّل. اتركه فارغًا لإخفائه.",
    bannerPlaceholder: "مثال: صيانة مجدولة الليلة 11:00–11:30 بتوقيت الخليج. قد يتأخر النشر.",
    preview: "معاينة",
    saveBanner: "نشر الشريط",
    clearBanner: "إزالة الشريط",
    bannerSaved: "تم تحديث الشريط",
    bannerCleared: "تمت إزالة الشريط",
    overrides: "استثناءات المؤسسات",
    overridesHint: "مؤسسات لها مفاتيحها الخاصة. عدّلها من صفحة المؤسسة.",
    noOverrides: "لا توجد مؤسسة لديها استثناءات.",
    effective: "الساري",
    col: { org: "المؤسسة", overrides: "الاستثناءات", effective: "الساري" },
  },
  banner: {
    label: "إشعار المنصة",
    dismiss: "إخفاء الإشعار",
    switchPill: "مفتاح إيقاف مفعّل",
  },
  denied: "ليس لديك وصول إلى لوحة الإدارة.",
  plansAdmin: {
    title: "الخطط والحدود",
    description: "ميزات وحدود كل خطة. تعديل الخطة ينطبق على كل المؤسسات المشتركة فيها ما لم يوجد تخصيص.",
    version: "الإصدار {{version}}",
    orgCount_zero: "المؤسسات: {{count}}",
    orgCount_one: "المؤسسات: {{count}}",
    orgCount_two: "المؤسسات: {{count}}",
    orgCount_few: "المؤسسات: {{count}}",
    orgCount_many: "المؤسسات: {{count}}",
    orgCount_other: "المؤسسات: {{count}}",
    features: "الميزات",
    limits: "الحدود",
    feature: "الميزة",
    limit: "الحد",
    unlimitedHint: "فارغ = غير محدود",
    trialDays: "مدة الفترة التجريبية (أيام)",
    review: "مراجعة التغييرات",
    reset: "تجاهل",
    noChanges: "لا تغييرات بعد",
    changesCount_zero: "التغييرات: {{count}}",
    changesCount_one: "التغييرات: {{count}}",
    changesCount_two: "التغييرات: {{count}}",
    changesCount_few: "التغييرات: {{count}}",
    changesCount_many: "التغييرات: {{count}}",
    changesCount_other: "التغييرات: {{count}}",
    reviewTitle: "حفظ تغييرات الخطط",
    reviewBody: "تحصل كل مؤسسة على هذه الخطط على الميزات والحدود الجديدة (ما لم يوجد تخصيص).",
    saved: "تم حفظ الخطط",
    invalidLimit: "أدخل رقماً 0 أو أكثر، أو اتركه فارغاً لغير محدود",
    invalidTrial: "من 1 إلى 365 يوماً",
    log: "سجل التغييرات",
    logTitle: "سجل تغييرات الصلاحيات",
    logDescription: "كل تغيير في الخطط والصلاحيات: من ومتى وقبل ← بعد والسبب.",
    logEmpty: "لا تغييرات بعد",
    filterOrg: "المؤسسة",
    filterPlan: "الخطة",
    any: "الكل",
    by: "بواسطة {{who}}",
    system: "النظام",
    actions: {
      plan_updated: "تعديل خطة",
      org_plan_set: "تغيير الخطة",
      org_status_set: "تغيير الحالة",
      org_trial_extended: "تمديد الفترة التجريبية",
      org_trial_ended: "إنهاء الفترة التجريبية",
      override_created: "إضافة تخصيص",
      override_revoked: "إلغاء تخصيص",
      plan_bulk_applied: "تطبيق خطة (جماعي)",
      ai_key_rotated: "تدوير مفتاح الذكاء الاصطناعي",
      ai_key_provisioned: "إنشاء مفتاح الذكاء الاصطناعي",
      backfill: "ترحيل",
    },
    org: {
      back: "كل المؤسسات",
      title: "الخطة والصلاحيات",
      assigned: "الخطة المعيّنة",
      effective: "الخطة الفعلية",
      status: "الحالة",
      planPanel: "الخطة",
      save: "حفظ",
      saveTitle: "تغيير خطة {{org}}",
      saveBody: "تتغير الميزات والحدود فوراً.",
      saved: "تم الحفظ",
      trial: "الفترة التجريبية",
      trialEnds: "تنتهي الفترة التجريبية في {{date}}",
      trialEnded: "انتهت الفترة التجريبية: المؤسسة على الخطة المجانية",
      noTrial: "ليست في فترة تجريبية",
      extendDays: "أيام",
      extend: "تمديد الفترة التجريبية",
      end: "إنهاء الفترة التجريبية الآن",
      extendTitle: "تمديد الفترة التجريبية لـ{{org}} بمقدار {{days}} يوماً",
      endTitle: "إنهاء الفترة التجريبية لـ{{org}} الآن",
      endBody: "تنتقل المؤسسة إلى الخطة المجانية فوراً.",
      usage: "الاستخدام مقابل الحدود",
      history: "السجل",
      historyOf: "سجل {{what}}",
      overrides: "التخصيصات",
      overridesHint: "خصّص ميزة أو حداً واحداً لهذه المؤسسة (انتهاء اختياري، والسبب مطلوب).",
      noOverrides: "لا توجد تخصيصات",
      addOverride: "إضافة تخصيص",
      kind: "النوع",
      kindFeature: "ميزة",
      kindLimit: "حد",
      key: "أيّها",
      value: "القيمة",
      on: "تشغيل",
      off: "إيقاف",
      expires: "ينتهي (اختياري)",
      never: "أبداً",
      expiresOn: "حتى {{date}}",
      revoke: "إلغاء",
      revokeTitle: "إلغاء تخصيص {{what}}",
      state: { active: "فعّال", expired: "منتهٍ", revoked: "ملغى" },
      createTitle: "إضافة تخصيص لـ{{org}}",
      errors: { key: "اختر ميزة أو حداً", value: "أدخل رقماً 0 أو أكثر، أو اتركه فارغاً لغير محدود", expiresAt: "يجب أن يكون تاريخ الانتهاء في المستقبل" },
      ai: "ميزانية ومفتاح الذكاء الاصطناعي",
      aiHint: "المفتاح الافتراضي للمؤسسة في LiteLLM: ميزانيته الشهرية هي حد ميزانية الذكاء الاصطناعي.",
      mode: { virtual_key: "مفتاح افتراضي خاص", global_key: "مفتاح مشترك (إسناد الإنفاق فقط)" },
      keyAlias: "اسم المفتاح",
      keyHint: "المفتاح",
      rpm: "طلبات في الدقيقة",
      tpm: "رموز في الدقيقة",
      spend: "الإنفاق هذا الشهر",
      exceeded: "نفدت الميزانية",
      resets: "تتجدد {{date}}",
      rotate: "تدوير المفتاح",
      rotateTitle: "تدوير مفتاح الذكاء الاصطناعي لـ{{org}}",
      rotateBody: "يُصدر مفتاح جديد ويتوقف القديم عن العمل.",
      rotated: "تم تدوير المفتاح",
      editBudget: "تعديل الميزانية",
      budgetTitle: "تحديد ميزانية الذكاء الاصطناعي الشهرية لـ{{org}}",
      budgetBody: "تُحفظ كتخصيص لحد ميزانية الذكاء الاصطناعي (دولار شهرياً؛ فارغ = غير محدود).",
      budgetLabel: "ميزانية الذكاء الاصطناعي الشهرية (دولار)",
      openLog: "سجل تغييرات هذه المؤسسة",
    },
  },
};
